import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';
import { getBaseUrl } from '../../utils/client';

// Served from /public for a stable, share-friendly OG URL — bundled
// `/_next/static/media/...` paths change on every deploy and are
// rejected by some social-preview proxies.
const OG_IMAGE_PATH = '/phonics-wizard-og.png';
import {
  CURRICULA,
  CURRICULUM_MARKETING,
  CurriculumEntry,
} from '../../utils/phonicsLandingPages';

const PROGRAM_RAIL_NAMES = [
  'UFLI',
  'HMH Into Reading',
  'Bluebonnet',
  'Heggerty',
  'CKLA',
  'Fundations',
  'Wonders',
];

interface Props {
  curricula: CurriculumEntry[];
}

/**
 * Hub-page "Book a demo" CTAs route to the existing quotes page rather
 * than the signup flow — this page targets curriculum admins, who book a
 * demo before they ever see signup.
 */
const BOOK_A_DEMO_HREF = '/quotes';

const PhonicsPracticeIndexPage = ({ curricula }: Props) => {
  const ogImageUrl = `${getBaseUrl()}${OG_IMAGE_PATH}`;
  return (
    <LandingPageLayout
      title="Phonics Practice — Curipod"
      ogTitle="Phonics Practice — encoding activities for every K-5 curriculum"
      subTitle="3,000+ ready-to-use phonics activities mapped to UFLI, HMH Into Reading, Bluebonnet, Heggerty, CKLA, Wilson Fundations, and Wonders."
      imageUrl={ogImageUrl}
      disableHeadline
    >
      <Hero>
        <HeroInner>
          <HeroH1>
            Phonics practice,
            <br />
            <HeroAccent>that actually sticks.</HeroAccent>
          </HeroH1>
          <HeroLede>
            Students hear the sound, spell the words, get instant feedback, read to each
            other, and try again — for every lesson in your{' '}
            <strong>K-5 curriculum</strong>.
          </HeroLede>
          <HeroLede>
            <strong>3,000+</strong> ready-to-use activities mapped to the major K-5
            phonics programs — UFLI, HMH Into Reading, Bluebonnet, Heggerty, CKLA, Wilson
            Fundations, and Wonders.
          </HeroLede>
          <HeroCtas>
            <PrimaryButton as="a" href={BOOK_A_DEMO_HREF}>
              Book a demo
            </PrimaryButton>
            <GhostButton as="a" href="#curricula">
              Browse activities
            </GhostButton>
          </HeroCtas>
          <HeroNote>
            <NoteDot /> No extra prep — drops right into your scope &amp; sequence.
          </HeroNote>
        </HeroInner>
      </Hero>

      <Rail>
        <RailInner>
          <RailLabel>Mapped to the programs you already teach</RailLabel>
          <RailNames>
            {PROGRAM_RAIL_NAMES.map(name => (
              <RailName key={name}>{name}</RailName>
            ))}
          </RailNames>
        </RailInner>
      </Rail>

      <SectionWhite id="how">
        <Wrap>
          <SecHead>
            <Eyebrow>How it works</Eyebrow>
            <SecH2>Whole-class phonics practice that works.</SecH2>
            <SecLead>
              Rigorous, engaging practice, together. Aligned to the science of reading,
              and complementing — not replacing — your curriculum.
            </SecLead>
          </SecHead>
          <Steps>
            <Step>
              <StepNum>1</StepNum>
              <StepH3>Hear the sound</StepH3>
              <StepP>
                Teacher introduces the target grapheme. Students listen and identify.
              </StepP>
            </Step>
            <Step $accent>
              <StepNum $accent>2</StepNum>
              <StepH3>Spell &amp; get feedback</StepH3>
              <StepP>
                Every student writes words with the target sound. Each one gets instant
                feedback and a score.
              </StepP>
            </Step>
            <Step>
              <StepNum>3</StepNum>
              <StepH3>Read aloud</StepH3>
              <StepP>Students share their words with a partner or the whole class.</StepP>
            </Step>
            <Step>
              <StepNum>4</StepNum>
              <StepH3>Learn &amp; try again</StepH3>
              <StepP>
                Teacher sees who got it. Students hear new words from peers — then try
                again with what they&apos;ve learned.
              </StepP>
            </Step>
          </Steps>
        </Wrap>
      </SectionWhite>

      <Band id="science">
        <BandGrid>
          <BandCard>
            <BandIcon $variant="check">✓</BandIcon>
            <BandH3>Aligned to the science of reading</BandH3>
            <BandP>
              Built on the encoding research: students segment, choose graphemes, write,
              and read,{' '}
              <em>
                hitting every step the science says builds durable phonics knowledge.
              </em>
            </BandP>
          </BandCard>
          <BandCard>
            <BandIcon $variant="arrow">→</BandIcon>
            <BandH3>Fits right after your direct instruction</BandH3>
            <BandP>
              Drops in after the teacher introduces the new pattern. Functions as the
              productive application step.{' '}
              <em>Students search their lexicon, you see who&apos;s stuck.</em>
            </BandP>
          </BandCard>
        </BandGrid>
      </Band>

      <Section id="curricula">
        <Wrap>
          <SecHead>
            <Eyebrow>Find yours</Eyebrow>
            <SecH2>
              Phonics practice that fits your curriculum&apos;s scope and sequence.
            </SecH2>
            <SecLead>
              Every activity mapped to the curriculum your school already teaches. Find
              yours below.
            </SecLead>
          </SecHead>

          <CurrGrid>
            {curricula.map(c => {
              const meta = CURRICULUM_MARKETING[c.value];
              if (!meta) return null;
              return (
                <Link key={c.slug} href={`/phonics-practice/${c.slug}`} passHref>
                  <CcLink>
                    <CcTag>{meta.tag}</CcTag>
                    <CcH3>{c.displayName}</CcH3>
                    <CcDesc>{meta.description}</CcDesc>
                    <CcFoot>
                      <CcCount>
                        <CcCountNum>{meta.lessonCount}</CcCountNum>
                        <CcCountLabel>phonics lessons</CcCountLabel>
                      </CcCount>
                      <CcLinkLabel>Browse {meta.browseShortLabel} →</CcLinkLabel>
                    </CcFoot>
                  </CcLink>
                </Link>
              );
            })}

            <Link href="/contact-us" passHref>
              <CcRequest>
                <CcTag>Don&apos;t see yours?</CcTag>
                <CcH3>Tell us what you teach</CcH3>
                <CcDesc>
                  We add curricula based on what teachers ask for. Tell us yours and
                  we&apos;ll prioritize it.
                </CcDesc>
                <CcLinkLabel $request>Request a curriculum →</CcLinkLabel>
              </CcRequest>
            </Link>
          </CurrGrid>
        </Wrap>
      </Section>

      <Cta>
        <CtaBlob $position="left" />
        <CtaBlob $position="right" />
        <CtaH2>See it inside your curriculum.</CtaH2>
        <CtaP>
          Book a demo and we&apos;ll show you how phonics practice maps into the lessons
          you already teach.
        </CtaP>
        <CtaRow>
          <CtaPrimary as="a" href={BOOK_A_DEMO_HREF}>
            Book a demo
          </CtaPrimary>
          <CtaGhost as="a" href="#curricula">
            Browse activities
          </CtaGhost>
        </CtaRow>
      </Cta>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      curricula: CURRICULA,
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    } as Props,
  };
};

export default PhonicsPracticeIndexPage;

/* ============================ styled ============================ */

const Hero = styled.section`
  max-width: 880px;
  margin: 0 auto;
  text-align: center;
  padding: 100px 32px 88px;
  background: ${theme.colors.paleBeige};
  max-width: 100%;

  @media (max-width: 992px) {
    padding: 64px 22px 56px;
  }
`;

const HeroInner = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;

const HeroH1 = styled.h1`
  font-size: clamp(40px, 6.2vw, 74px);
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.03em;
  margin: 0 0 22px;
  color: ${theme.colors.black};
  text-wrap: balance;

  @media (max-width: 992px) {
    font-size: clamp(36px, 8vw, 52px);
  }
`;

const HeroAccent = styled.span`
  color: ${theme.colors.orangeDark};
`;

const HeroLede = styled.p`
  font-size: 19px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  max-width: 640px;
  margin: 0 auto 18px;

  strong {
    color: ${theme.colors.black};
    font-weight: 700;
  }
`;

const HeroCtas = styled.div`
  display: flex;
  gap: 12px;
  margin: 26px 0 22px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background: ${theme.colors.orange};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 900;
  padding: 14px 24px;
  line-height: 1.1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 8px 20px -8px rgba(229, 131, 50, 0.6);
  transition: transform 200ms ease, background-color 200ms ease;

  &:hover {
    background: ${theme.colors.orangeDark};
    transform: translateY(-1px);
  }
`;

const GhostButton = styled.button`
  background: transparent;
  color: ${theme.colors.black};
  border: 2px solid ${theme.colors.black};
  border-radius: 10px;
  font-size: 16px;
  font-weight: 900;
  padding: 12px 22px;
  line-height: 1.1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover {
    background: ${theme.colors.black};
    color: #fff;
  }
`;

const HeroNote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.7);
`;

const NoteDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${theme.colors.successText};
  box-shadow: 0 0 0 4px ${theme.colors.successBg};
`;

const Rail = styled.section`
  background: rgba(255, 255, 255, 0.45);
  border-top: 1px solid rgba(40, 47, 51, 0.07);
  border-bottom: 1px solid rgba(40, 47, 51, 0.07);
`;

const RailInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 26px 32px;
  display: flex;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 22px;
  }
`;

const RailLabel = styled.div`
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(40, 47, 51, 0.5);
  max-width: 150px;
  line-height: 1.4;
`;

const RailNames = styled.div`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  flex: 1;
  align-items: center;
`;

const RailName = styled.span`
  font-weight: 900;
  font-size: 17px;
  color: ${theme.colors.black};
  opacity: 0.5;
  letter-spacing: -0.01em;
`;

const Section = styled.section`
  padding: 96px 32px;
  background: ${theme.colors.paleBeige};

  @media (max-width: 600px) {
    padding: 64px 22px;
  }
`;

const SectionWhite = styled(Section)`
  background: #fff;
`;

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const SecHead = styled.div`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 56px;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
`;

const SecH2 = styled.h2`
  font-size: clamp(32px, 4vw, 46px);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.06;
  margin: 14px 0;
  color: ${theme.colors.black};
  text-wrap: balance;
`;

const SecLead = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;
  text-wrap: pretty;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div<{ $accent?: boolean }>`
  background: ${theme.colors.paleBeige};
  border-radius: 18px;
  padding: 26px 22px 24px;
  transition: transform 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  }
`;

const StepNum = styled.div<{ $accent?: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: ${({ $accent }) => ($accent ? theme.colors.orange : theme.colors.black)};
  color: #fff;
  font-weight: 900;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
`;

const StepH3 = styled.h3`
  font-size: 20px;
  font-weight: 900;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};
`;

const StepP = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;
`;

const Band = styled.section`
  background: ${theme.colors.black};
  color: #fff;
  padding: 86px 32px;

  @media (max-width: 600px) {
    padding: 64px 22px;
  }
`;

const BandGrid = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BandCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 32px 30px;
`;

const BandIcon = styled.div<{ $variant: 'check' | 'arrow' }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 18px;
  color: #fff;
  background: ${({ $variant }) =>
    $variant === 'check' ? theme.colors.successText : theme.colors.orange};
`;

const BandH3 = styled.h3`
  font-size: 23px;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin: 0 0 12px;
  color: #fff;
`;

const BandP = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.55;
  margin: 0;

  em {
    color: #fff;
    font-style: normal;
  }
`;

const CurrGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CcLink = styled.a`
  background: #fff;
  border-radius: 18px;
  padding: 26px 24px 22px;
  box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  border: 1px solid rgba(40, 47, 51, 0.04);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: ${theme.colors.black};
  transition: transform 200ms ease, box-shadow 200ms ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(40, 47, 51, 0.14);
  }
`;

const CcRequest = styled.a`
  background: transparent;
  border: 2px dashed rgba(40, 47, 51, 0.22);
  border-radius: 18px;
  padding: 26px 24px 22px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-decoration: none;
  color: ${theme.colors.black};
  transition: transform 200ms ease, border-color 200ms ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    border-color: ${theme.colors.orange};
  }
`;

const CcTag = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
  margin-bottom: 14px;
`;

const CcH3 = styled.h3`
  font-size: 23px;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin: 0 0 10px;
  color: ${theme.colors.black};
`;

const CcDesc = styled.p`
  font-size: 14.5px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0 0 22px;
  flex: 1;
`;

const CcFoot = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-top: 18px;
  border-top: 1px solid rgba(40, 47, 51, 0.08);
`;

const CcCount = styled.div`
  line-height: 1;
`;

const CcCountNum = styled.div`
  font-size: 34px;
  font-weight: 900;
  color: ${theme.colors.black};
  letter-spacing: -0.02em;
`;

const CcCountLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.5);
  margin-top: 4px;
`;

const CcLinkLabel = styled.span<{ $request?: boolean }>`
  font-size: 13.5px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  ${({ $request }) => $request && 'margin-top: 18px;'}
`;

const Cta = styled.section`
  background: ${theme.colors.orange};
  color: #fff;
  padding: 92px 32px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const CtaBlob = styled.span<{ $position: 'left' | 'right' }>`
  position: absolute;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  ${({ $position }) =>
    $position === 'left'
      ? 'width: 340px; height: 340px; top: -120px; left: -90px;'
      : 'width: 260px; height: 260px; bottom: -110px; right: -60px;'}
`;

const CtaH2 = styled.h2`
  font-size: clamp(34px, 4.6vw, 52px);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 14px;
  color: #fff;
  text-wrap: balance;
  position: relative;
`;

const CtaP = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  max-width: 560px;
  margin: 0 auto 30px;
  line-height: 1.5;
  position: relative;
`;

const CtaRow = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
`;

const CtaPrimary = styled(PrimaryButton)`
  background: #fff;
  color: ${theme.colors.orangeDark};
  box-shadow: 0 12px 28px -10px rgba(0, 0, 0, 0.3);

  &:hover {
    background: ${theme.colors.black};
    color: #fff;
  }
`;

const CtaGhost = styled(GhostButton)`
  border-color: rgba(255, 255, 255, 0.85);
  color: #fff;

  &:hover {
    background: #fff;
    color: ${theme.colors.orangeDark};
  }
`;
